import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const WhyChooseUsSection = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const scrollToContact = () => {
    document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCardClick = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const cards = [
    {
      emoji: "💰",
      title: "Save Up to 40%",
      description: "Our regional model eliminates expensive travel and accommodation costs.",
      expandedContent: (
        <div className="pt-4 border-t border-border/50">
          <p className="text-sm font-semibold text-primary mb-3">Coast-to-Coast Coverage</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="p-2 rounded-lg bg-muted/50">
              <div className="font-bold">East</div>
              <div className="text-muted-foreground">40+ artists</div>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <div className="font-bold">Central</div>
              <div className="text-muted-foreground">45+ artists</div>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <div className="font-bold">West</div>
              <div className="text-muted-foreground">45+ artists</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Local musicians = no travel fees for you</p>
        </div>
      ),
    },
    {
      emoji: "📅",
      title: "Multi-Day & Bulk Events",
      description: "Book musicians for wedding weekends, conferences, or recurring events in one go.",
      expandedContent: (
        <div className="pt-4 border-t border-border/50">
          <div className="space-y-2 text-left text-sm">
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Multi-day wedding packages</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Corporate conference series</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Bundled pricing discounts</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Single point of contact</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      emoji: "🎯",
      title: "Perfect Match Guarantee",
      description: "AI-powered curation ensures every musician understands your vision.",
      expandedContent: (
        <div className="pt-4 border-t border-border/50">
          <div className="space-y-2 text-left text-sm">
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Cultural analysis of your event</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Vibe-matched musician selection</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Curated setlist recommendations</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Pre-event coordination calls</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      emoji: "🛡️",
      title: "Stress-Free Coordination",
      description: "Our regional managers handle everything—you just enjoy.",
      expandedContent: (
        <div className="pt-4 border-t border-border/50">
          <div className="space-y-2 text-left text-sm">
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>On-site regional manager</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>DJ & AV team coordination</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Equipment & mic checks</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              <span>Certificate of Insurance</span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
              Why Choose The Lost Strings?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
              We've reimagined how event music should work—faster, smarter, and more affordable.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {cards.map((card, index) => (
              <div 
                key={index}
                className="group cursor-pointer"
                onClick={() => handleCardClick(index)}
              >
                <div 
                  className={cn(
                    "text-center p-5 sm:p-6 rounded-2xl bg-card border transition-all duration-500 h-full",
                    expandedCard === index 
                      ? "border-primary/50 shadow-xl shadow-primary/10 -translate-y-2" 
                      : "border-border lg:group-hover:border-primary/50 lg:group-hover:shadow-xl lg:group-hover:shadow-primary/10 lg:group-hover:-translate-y-2"
                  )}
                >
                  <div 
                    className={cn(
                      "w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-primary/10 flex items-center justify-center transition-all duration-300",
                      expandedCard === index 
                        ? "scale-110 bg-primary/20" 
                        : "lg:group-hover:scale-110 lg:group-hover:bg-primary/20"
                    )}
                  >
                    <span className="text-xl sm:text-2xl">{card.emoji}</span>
                  </div>
                  <h3 
                    className={cn(
                      "text-lg sm:text-xl font-bold mb-2 transition-colors",
                      expandedCard === index ? "text-primary" : "lg:group-hover:text-primary"
                    )}
                  >
                    {card.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">
                    {card.description}
                  </p>
                  
                  {/* Mobile: Tap indicator */}
                  <div className="lg:hidden flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <span>Tap for details</span>
                    <ChevronDown 
                      className={cn(
                        "w-4 h-4 transition-transform duration-300",
                        expandedCard === index && "rotate-180"
                      )} 
                    />
                  </div>
                  
                  {/* Expanded content - Mobile: tap toggle, Desktop: hover */}
                  <div 
                    className={cn(
                      "overflow-hidden transition-all duration-500 ease-in-out",
                      // Mobile/tablet: controlled by tap
                      expandedCard === index ? "max-h-96" : "max-h-0",
                      // Desktop: controlled by hover
                      "lg:max-h-0 lg:group-hover:max-h-96"
                    )}
                  >
                    {card.expandedContent}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Interaction hint */}
          <p className="text-center text-xs sm:text-sm text-muted-foreground mt-6 animate-pulse">
            <span className="hidden lg:inline">Hover over each card to learn more</span>
            <span className="lg:hidden">Tap each card to learn more</span>
          </p>

          {/* Mid-page CTA */}
          <div className="text-center mt-8 sm:mt-12">
            <Button 
              size="lg" 
              variant="outline"
              onClick={scrollToContact}
              className="w-full sm:w-auto group"
            >
              See How Much You Can Save
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;